'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { fetchSmartAlerts } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

const severityStyles: Record<string, string> = {
  critical: 'border-l-negative bg-negative-bg/40',
  warning: 'border-l-warning bg-warning-bg/40',
  info: 'border-l-info bg-info-bg/40',
};

const dotStyles: Record<string, string> = {
  critical: 'bg-negative',
  warning: 'bg-warning',
  info: 'bg-info',
};

export default function SmartAlertsPanel() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(() => {
    fetchSmartAlerts().then((result) => {
      if (result) setAlerts(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadAlerts();

    const supabase = createClient();
    const channel = supabase
      .channel('alerts-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory_snapshots' }, loadAlerts)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadAlerts]);

  if (loading) {
    return (
      <div className="p-4 space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={`sk-${i}`} className="h-12 bg-muted/40 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!alerts.length) {
    return (
      <div className="p-6 text-center">
        <p className="text-xs text-positive font-500">✓ No active alerts</p>
        <p className="text-2xs text-muted-foreground mt-1">All inventory levels are healthy</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border/50">
      {alerts.map((alert) => (
        <div key={alert.id} className={`px-4 py-3 border-l-2 ${severityStyles[alert.severity] || severityStyles.info} cursor-pointer row-hover transition-colors`}>
          <div className="flex items-start gap-2">
            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dotStyles[alert.severity] || dotStyles.info}`} />
            <div className="min-w-0">
              <p className="text-xs font-600 text-foreground truncate">{alert.message}</p>
              <p className="text-2xs text-muted-foreground">{alert.detail}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}